import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { useAuth } from '../../lib/auth'
import { formatDate, formatMass, titleCase } from '../../lib/format'
import type { ComplianceSummary, HazardReport, IncidentReport, ProductionLog, UserProfile } from '../../types'
import {
  Alert, Badge, Button, Card, CardBody, CardHeader, EmptyState, Skeleton, StatusBadge,
} from '../../components/ui'
import { RangeChips, WeeklyBarChart, WeeklyLineChart } from '../../components/charts'
import { AlertIcon, ChartIcon, CheckIcon, IdIcon, ShieldIcon, UserIcon } from '../../components/icons'
import '../miner/dashboard.css'
import './onboarding.css'

interface LeaderSeries {
  weeks: string[]
  production_kg: number[]
  loads: number[]
  incidents: number[]
  hazards: number[]
}

interface LeaderDashboard {
  ok: boolean
  series?: LeaderSeries
  user: UserProfile
  kpis: {
    team_size: number
    kyc_pending: number
    production_pending: number
    incidents_open: number
    hazards_open: number
  }
  recent_productions: ProductionLog[]
  recent_incidents: IncidentReport[]
  recent_hazards: HazardReport[]
  compliance: ComplianceSummary | null
}

export default function LeaderDashboard() {
  const [tab, setTab] = useState<'summary' | 'trends'>('summary')
  const [weeks, setWeeks] = useState(8)
  const { user } = useAuth()
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['leader', 'dashboard', weeks],
    queryFn: () => api.get<LeaderDashboard>(`/leader/dashboard?weeks=${weeks}`),
  })

  const firstName = (user?.full_name ?? user?.email ?? '').split(/[\s@]/)[0]
  const k = data?.kpis

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Cooperative overview{firstName ? <>, <span className="welcome__name">{firstName}</span></> : ''}</h1>
          <p className="page-head__sub">What your team has submitted and what needs your review.</p>
        </div>
      </div>

      {isError && <Alert tone="danger">{(error as Error).message}</Alert>}

      {data?.compliance && !data.compliance.is_approved && (
        <ComplianceBanner compliance={data.compliance} />
      )}

      {/* Summary = KPIs + recent activity; Trends = what's happening over
          time, charted. Mirrors the admin dashboard's tab pattern. */}
      <div className="tab-scroll" style={{ marginBottom: 'var(--sp-5)' }}>
        <Button variant={tab === 'summary' ? 'primary' : 'secondary'} size="sm" onClick={() => setTab('summary')}>
          Summary
        </Button>
        <Button variant={tab === 'trends' ? 'primary' : 'secondary'} size="sm" onClick={() => setTab('trends')}>
          Trends
        </Button>
      </div>

      {tab === 'trends' && (
        <div className="stack" style={{ gap: 'var(--sp-5)' }}>
          <RangeChips value={weeks} onChange={setWeeks} />
          <Card>
            <CardHeader title="Production per week" sub={`Volume logged by your team (kg), last ${weeks} weeks`} />
            <CardBody>
              {isLoading || !data?.series ? <Loading /> : (
                <WeeklyBarChart weeks={data.series.weeks} values={data.series.production_kg} />
              )}
            </CardBody>
          </Card>
          <Card>
            <CardHeader title="Loads per week" sub={`Loads dispatched, last ${weeks} weeks`} />
            <CardBody>
              {isLoading || !data?.series ? <Loading /> : (
                <WeeklyLineChart weeks={data.series.weeks} series={[{ label: 'Loads', values: data.series.loads }]} />
              )}
            </CardBody>
          </Card>
          <Card>
            <CardHeader title="Incidents & hazards per week" sub={`New safety reports, last ${weeks} weeks`} />
            <CardBody>
              {isLoading || !data?.series ? <Loading /> : (
                <WeeklyLineChart
                  weeks={data.series.weeks}
                  series={[
                    { label: 'Incidents', values: data.series.incidents },
                    { label: 'Hazards', values: data.series.hazards },
                  ]}
                />
              )}
            </CardBody>
          </Card>
        </div>
      )}

      {tab === 'summary' && (<>
      {/* Ordered by urgency of action rather than by data source. */}
      <div className="grid grid--stats" style={{ marginBottom: 'var(--sp-6)' }}>
        <Kpi label="Awaiting review" value={k?.production_pending} loading={isLoading}
             meta="production logs" to="/production" icon={<ChartIcon />} urgent={!!k?.production_pending} />
        <Kpi label="KYC pending" value={k?.kyc_pending} loading={isLoading}
             meta="documents to verify" to="/kyc" icon={<IdIcon />} urgent={!!k?.kyc_pending} />
        <Kpi label="Open incidents" value={k?.incidents_open} loading={isLoading}
             meta="need acknowledgement" to="/safety" icon={<AlertIcon />} urgent={!!k?.incidents_open} />
        <Kpi label="Open hazards" value={k?.hazards_open} loading={isLoading}
             meta="need acknowledgement" to="/safety" icon={<ShieldIcon />} urgent={!!k?.hazards_open} />
        <Kpi label="Team members" value={k?.team_size} loading={isLoading}
             meta="in your cooperative" to="/team" icon={<UserIcon />} />
      </div>

      <div className="grid grid--2">
        <Card>
          <CardHeader title="Recent production"
                      action={<Link to="/production" style={{ fontSize: 'var(--text-sm)' }}>Review all</Link>} />
          <CardBody flush>
            {isLoading ? (
              <Loading />
            ) : !data?.recent_productions.length ? (
              <EmptyState icon={<ChartIcon size={22} />} title="Nothing submitted yet"
                          body="Production logged by your team appears here for review." />
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead><tr><th>Mineral</th><th className="table__num">Volume</th><th>Period</th><th>Status</th></tr></thead>
                  <tbody>
                    {data.recent_productions.map((p, i) => (
                      <tr key={p.id ?? i}>
                        <td>{titleCase(p.mineral_type)}</td>
                        <td className="table__num">{formatMass(p.volume_kg)}</td>
                        <td>{formatDate(p.period_start)}</td>
                        <td><StatusBadge status={p.status || 'submitted'} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Safety reports"
                      action={<Link to="/safety" style={{ fontSize: 'var(--text-sm)' }}>Triage</Link>} />
          <CardBody flush>
            {isLoading ? (
              <Loading />
            ) : !data?.recent_incidents.length && !data?.recent_hazards.length ? (
              <EmptyState icon={<CheckIcon size={22} />} title="No open reports"
                          body="Incidents and hazards raised by your team will appear here." />
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead><tr><th>Kind</th><th>Type</th><th>Severity / risk</th><th>Status</th></tr></thead>
                  <tbody>
                    {data.recent_incidents.map((r, i) => (
                      <tr key={`i-${r.id ?? i}`}>
                        <td><span className="badge badge--danger">Incident</span></td>
                        <td>{titleCase(r.incident_type)}</td>
                        <td><StatusBadge status={r.severity} /></td>
                        <td><StatusBadge status={r.status} /></td>
                      </tr>
                    ))}
                    {data.recent_hazards.map((r, i) => (
                      <tr key={`h-${r.id ?? i}`}>
                        <td><span className="badge badge--warning">Hazard</span></td>
                        <td>{titleCase(r.hazard_type)}</td>
                        <td><StatusBadge status={r.risk_level} /></td>
                        <td><StatusBadge status={r.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Card>
      </div>
      </>)}
    </>
  )
}

function Loading() {
  return (
    <div style={{ padding: 'var(--sp-6)' }} className="stack">
      <Skeleton h={18} /><Skeleton h={18} w="74%" /><Skeleton h={18} w="60%" />
    </div>
  )
}

function Kpi({
  label, value, meta, icon, to, loading, urgent,
}: {
  label: string; value?: number; meta?: string; icon?: React.ReactNode
  to: string; loading: boolean; urgent?: boolean
}) {
  return (
    <Link to={to} style={{ textDecoration: 'none', color: 'inherit' }}>
      <Card hover>
        <div className="stat">
          <div className="stat__top">
            <span className="stat__label">{label}</span>
            {icon && (
              <span className="stat__icon"
                    style={urgent ? { background: 'var(--warning-bg)', color: 'var(--warning)' } : undefined}>
                {icon}
              </span>
            )}
          </div>
          {loading ? <Skeleton h={34} w="50%" />
                   : <div className="stat__value">{value ?? 0}</div>}
          {meta && <div className="stat__meta">{meta}</div>}
        </div>
      </Card>
    </Link>
  )
}

function ComplianceBanner({ compliance }: { compliance: ComplianceSummary }) {
  const total = Object.keys(compliance.progress.items).length
  const done = Object.values(compliance.progress.items).filter(Boolean).length
  const status = compliance.registration_status
  const tone = status === 'changes_requested' ? 'danger' : status === 'pending_approval' ? 'warning' : 'accent'
  const statusLabel =
    status === 'changes_requested' ? 'Changes requested'
    : status === 'pending_approval' ? 'Awaiting approval'
    : 'Setup incomplete'

  return (
    <Card className="stack" style={{ marginBottom: 'var(--sp-6)' }}>
      <CardHeader
        title="Complete your compliance setup"
        sub={`${done} of ${total} requirements complete — your organisation cannot register sites, log production or dispatch loads until this is approved.`}
        action={<Link to="/onboarding"><Badge tone={tone} dot>{statusLabel}</Badge></Link>}
      />
      <CardBody>
        <div className="onboard">
          <div className="onboard__bar">
            <div className="onboard__fill" style={{ width: `${(done / total) * 100}%` }} />
          </div>
          <Link to="/onboarding" className="onb-banner__cta">
            <ShieldIcon size={16} /> Continue compliance setup
          </Link>
        </div>
      </CardBody>
    </Card>
  )
}
